import { takeLatest, put } from 'redux-saga/effects'
import { createReducer, makeAction, makeKeys, urlWithPrefix } from './helper'
import { httpPost, httpPut, httpGet, httpDelete } from 'core/restClient'
import { appLoading, appLoaded, toastError, toastSuccess } from './app'

const GET_PROVINCE_WITH_RELATIVE = makeKeys('GET_PROVINCE_WITH_RELATIVE')
const GET_PROVINCE = makeKeys('GET_PROVINCE')
const GET_DISTRICT = makeKeys('GET_DISTRICT')
const GET_SUB_DISTRICT = makeKeys('GET_SUB_DISTRICT')
const CREATE_PROVINCE = makeKeys('CREATE_PROVINCE')
const UPDATE_PROVINCE = makeKeys('UPDATE_PROVINCE')
const CREATE_DISTRICT = makeKeys('CREATE_DISTRICT')
const UPDATE_DISTRICT = makeKeys('UPDATE_DISTRICT')
const CREATE_SUB_DISTRICT = makeKeys('CREATE_SUB_DISTRICT')
const UPDATE_SUB_DISTRICT = makeKeys('UPDATE_SUB_DISTRICT')

export const getProvinceWithRelative = makeAction(GET_PROVINCE_WITH_RELATIVE.REQUEST)
export const getProvinces = makeAction(GET_PROVINCE.REQUEST)
export const getDistricts = makeAction(GET_DISTRICT.REQUEST, 'provinceId')
export const getSubDistricts = makeAction(GET_SUB_DISTRICT.REQUEST, 'provinceId', 'districtId')
export const createProvince = makeAction(CREATE_PROVINCE.REQUEST, 'data', 'onSuccess')
export const updateProvince = makeAction(UPDATE_PROVINCE.REQUEST, 'data', 'onSuccess')
export const createDistrict = makeAction(CREATE_DISTRICT.REQUEST, 'data', 'onSuccess')
export const updateDistrict = makeAction(UPDATE_DISTRICT.REQUEST, 'data', 'onSuccess')
export const createSubDistrict = makeAction(CREATE_SUB_DISTRICT.REQUEST, 'data', 'onSuccess')
export const updateSubDistrict = makeAction(UPDATE_SUB_DISTRICT.REQUEST, 'data', 'onSuccess')

const getProvinceWithRelativeSuccess = makeAction(GET_PROVINCE_WITH_RELATIVE.SUCCESS, 'data')
const getProvincesSuccess = makeAction(GET_PROVINCE.SUCCESS, 'data')
const getDistrictsSuccess = makeAction(GET_DISTRICT.SUCCESS, 'data')
const getSubDistrictsSuccess = makeAction(GET_SUB_DISTRICT.SUCCESS, 'data')

const dataManagementPath = path => path ? urlWithPrefix(`dataManagement/${path}`) : urlWithPrefix('dataManagement')
const provincePath = path => path ? dataManagementPath(`provinces/${path}`) : dataManagementPath('provinces')
const districtPath = (provinceId, path) => path ? provincePath(`${provinceId}/districts/${path}`) : provincePath(`${provinceId}/districts`)
const subDistrictPath = (provinceId, districtId, path) => path ? districtPath(provinceId, `${districtId}/subDistricts/${path}`) : districtPath(provinceId, `${districtId}/subDistricts`)

function* getProvinceWithRelativeRequest(action) {
  try {
    yield put(appLoading('Loading data.'))
    const response = yield httpGet(provincePath('relative'))
    yield put(appLoaded())
    if (response.status >= 200 && response.status < 300) {
      if (response.data) {
        yield put(getProvinceWithRelativeSuccess(response.data))
      }
    }
  } catch (e) {
    if (e && e.data && e.data.message) {
      yield put(toastError(e.data.message))
    } else {
      yield put(toastError('Load provinces data error!'))
    }
    yield put(appLoaded())
  }
}

function* getProvincesRequest(action) {
  try {
    const response = yield httpGet(provincePath())
    if (response.status >= 200 && response.status < 300) {
      if (response.data) {
        yield put(getProvincesSuccess(response.data))
      }
    }
  } catch (e) {
    if (e && e.data && e.data.message) {
      yield put(toastError(e.data.message))
    } else {
      yield put(toastError('Load provinces data error!'))
    }
  }
}

function* getDistrictsRequest(action) {
  const { provinceId } = action.payload
  try {
    const response = yield httpGet(districtPath(provinceId))
    if (response.status >= 200 && response.status < 300) {
      if (response.data) {
        yield put(getDistrictsSuccess(response.data))
      }
    }
  } catch (e) {
    if (e && e.data && e.data.message) {
      yield put(toastError(e.data.message))
    } else {
      yield put(toastError('Load district data error!'))
    }
  }
}

function* getSubDistrictsRequest(action) {
  const { provinceId, districtId } = action.payload
  try {
    const response = yield httpGet(subDistrictPath(provinceId, districtId))
    if (response.status >= 200 && response.status < 300) {
      if (response.data) {
        yield put(getSubDistrictsSuccess(response.data))
      }
    }
  } catch (e) {
    if (e && e.data && e.data.message) {
      yield put(toastError(e.data.message))
    } else {
      yield put(toastError('Load sub district data error!'))
    }
  }
}

function* createProvinceRequest(action) {
  const { data, onSuccess } = action.payload
  try {
    yield put(appLoading('Creating Province'))
    const response = yield httpPost(provincePath(), data)
    yield put(appLoaded())
    if (response.status >= 200 && response.status < 300) {
      if (response.data) {
        if (response.data.message) {
          yield put(toastSuccess(response.data.message))
          yield put(getProvinces())
          onSuccess()
        } else{
          yield put(toastSuccess('Create Province Success'))
        }
      }
    }
  } catch (e) {
    if (e && e.data && e.data.message) {
      yield put(toastError(e.data.message))
    } else {
      yield put(toastError('Create Province Error!'))
    }
    yield put(appLoaded())
  }
}

function* updateProvinceRequest(action) {
  const { data, onSuccess } = action.payload
  try {
    yield put(appLoading('Updating Province'))
    const response = yield httpPut(provincePath(data.id), data)
    yield put(appLoaded())
    if (response.status >= 200 && response.status < 300) {
      if (response.data) {
        yield put(toastSuccess('Update Province Success'))
        yield put(getProvinces())
        onSuccess()
      }
    }
  } catch (e) {
    if (e && e.data && e.data.message) {
      yield put(toastError(e.data.message))
    } else {
      yield put(toastError('Update Province Error!'))
    }
    yield put(appLoaded())
  }
}

function* createDistrictRequest(action) {
  const { data, onSuccess } = action.payload
  try {
    yield put(appLoading('Creating District'))
    const response = yield httpPost(districtPath(data.provinceId), data)
    yield put(appLoaded())
    if (response.status >= 200 && response.status < 300) {
      if (response.data) {
        if (response.data.message) {
          yield put(toastSuccess(response.data.message))
          yield put(getDistricts(data.provinceId))
          onSuccess()
        } else{
          yield put(toastSuccess('Create District Success'))
        }
      }
    }
  } catch (e) {
    if (e && e.data && e.data.message) {
      yield put(toastError(e.data.message))
    } else {
      yield put(toastError('Create District Error!'))
    }
    yield put(appLoaded())
  }
}

function* updateDistrictRequest(action) {
  const { data, onSuccess } = action.payload
  try {
    yield put(appLoading('Updating District'))
    const response = yield httpPut(districtPath(data.provinceId, data.id), data)
    yield put(appLoaded())
    if (response.status >= 200 && response.status < 300) {
      if (response.data) {
        yield put(toastSuccess('Update District Success'))
        yield put(getDistricts(data.provinceId))
        onSuccess()
      }
    }
  } catch (e) {
    if (e && e.data && e.data.message) {
      yield put(toastError(e.data.message))
    } else {
      yield put(toastError('Update District Error!'))
    }
    yield put(appLoaded())
  }
}

function* createSubDistrictRequest(action) {
  const { data, onSuccess } = action.payload
  try {
    yield put(appLoading('Creating Sub District'))
    const response = yield httpPost(subDistrictPath(data.provinceId, data.districtId), data)
    yield put(appLoaded())
    if (response.status >= 200 && response.status < 300) {
      if (response.data) {
        if (response.data.message) {
          yield put(toastSuccess(response.data.message))
          yield put(getSubDistricts(data.provinceId, data.districtId))
          onSuccess()
        } else{
          yield put(toastSuccess('Create Sub District Success'))
        }
      }
    }
  } catch (e) {
    if (e && e.data && e.data.message) {
      yield put(toastError(e.data.message))
    } else {
      yield put(toastError('Create Sub District Error!'))
    }
    yield put(appLoaded())
  }
}

function* updateSubDistrictRequest(action) {
  const { data, onSuccess } = action.payload
  try {
    yield put(appLoading('Updating Sub District'))
    const response = yield httpPut(subDistrictPath(data.provinceId, data.districtId, data.id), data)
    yield put(appLoaded())
    if (response.status >= 200 && response.status < 300) {
      if (response.data) {
        yield put(toastSuccess('Update Sub District Success'))
        yield put(getSubDistricts(data.provinceId, data.districtId))
        onSuccess()
      }
    }
  } catch (e) {
    if (e && e.data && e.data.message) {
      yield put(toastError(e.data.message))
    } else {
      yield put(toastError('Update Sub District Error!'))
    }
    yield put(appLoaded())
  }
}

export function* watchDataManagementSaga() {
  yield takeLatest(GET_PROVINCE_WITH_RELATIVE.REQUEST, getProvinceWithRelativeRequest)
  yield takeLatest(GET_PROVINCE.REQUEST, getProvincesRequest)
  yield takeLatest(GET_DISTRICT.REQUEST, getDistrictsRequest)
  yield takeLatest(GET_SUB_DISTRICT.REQUEST, getSubDistrictsRequest)
  yield takeLatest(CREATE_PROVINCE.REQUEST, createProvinceRequest)
  yield takeLatest(UPDATE_PROVINCE.REQUEST, updateProvinceRequest)
  yield takeLatest(CREATE_DISTRICT.REQUEST, createDistrictRequest)
  yield takeLatest(UPDATE_DISTRICT.REQUEST, updateDistrictRequest)
  yield takeLatest(CREATE_SUB_DISTRICT.REQUEST, createSubDistrictRequest)
  yield takeLatest(UPDATE_SUB_DISTRICT.REQUEST, updateSubDistrictRequest)
}

const initial = {
  provincesWithRelative: { data: [], loading: false },
  provinces: { data: [], loading: false },
  districts: { data: [], loading: false },
  subDistricts: { data: [], loading: false },
}

export default createReducer(initial, state => ({
  [GET_PROVINCE_WITH_RELATIVE.REQUEST]: () => ({
    ...state,
    provincesWithRelative: { data: [], loading: true }
  }),
  [GET_PROVINCE_WITH_RELATIVE.SUCCESS]: (data) => ({
    ...state,
    provincesWithRelative: { data: data.data, loading: false }
  }),
  [GET_PROVINCE.REQUEST]: () => ({
    ...state,
    provinces: { data: [], loading: true },
    districts: { data: [], loading: false },
    subDistricts: { data: [], loading: false },
  }),
  [GET_PROVINCE.SUCCESS]: (data) => ({
    ...state,
    provinces: { data: data.data, loading: false }
  }),
  [GET_DISTRICT.REQUEST]: () => ({
    ...state,
    districts: { data: [], loading: true },
    subDistricts: { data: [], loading: false },
  }),
  [GET_DISTRICT.SUCCESS]: (data) => ({
    ...state,
    districts: { data: data.data, loading: false }
  }),
  [GET_SUB_DISTRICT.REQUEST]: () => ({
    ...state,
    subDistricts: { data: [], loading: true }
  }),
  [GET_SUB_DISTRICT.SUCCESS]: (data) => ({
    ...state,
    subDistricts: { data: data.data, loading: false }
  })
}))